import type {
  WorkplaceRequestTypeField,
  WorkplaceRequestTypeFieldType
} from "@gettactic/api";
import { isImageOptionField } from "./image-option-field";

/**
 * Field types whose inputs are visually compact enough to sit two-per-row.
 * Everything else (long text, uploads, the seat picker) always claims a full
 * row because it either grows tall or needs the horizontal space.
 */
const COMPACT_FIELD_TYPES = new Set<WorkplaceRequestTypeFieldType>([
  "short_text",
  "single_select",
  "multi_select",
  "number",
  "date",
  "time",
  "person",
  "toggle"
]);

/**
 * A field with a long label or description would wrap awkwardly when squeezed
 * into a half-width column, so it is kept on its own row even if its input type
 * is otherwise compact. These thresholds are deliberately conservative.
 */
const MAX_LABEL_LENGTH = 24;
const MAX_DESCRIPTION_LENGTH = 48;

const isCompactField = (field: WorkplaceRequestTypeField): boolean => {
  if (!COMPACT_FIELD_TYPES.has(field.field_type)) return false;
  // A select rendered as an image picker shows large cards, not a dropdown.
  if (isImageOptionField(field)) return false;
  if ((field.name?.length ?? 0) > MAX_LABEL_LENGTH) return false;
  if ((field.description?.length ?? 0) > MAX_DESCRIPTION_LENGTH) return false;
  return true;
};

/**
 * Groups form fields into rows, pairing consecutive compact fields two-per-row
 * while leaving larger fields on their own row.
 *
 * The original field order is always preserved: only two *adjacent* compact
 * fields are ever paired, so the algorithm never reorders the form.
 */
export const packFormFields = (
  fields: WorkplaceRequestTypeField[]
): WorkplaceRequestTypeField[][] => {
  const rows: WorkplaceRequestTypeField[][] = [];
  let pendingCompact: WorkplaceRequestTypeField | null = null;

  const flushPending = () => {
    if (pendingCompact) {
      rows.push([pendingCompact]);
      pendingCompact = null;
    }
  };

  for (const field of fields) {
    if (!isCompactField(field)) {
      flushPending();
      rows.push([field]);
      continue;
    }

    if (pendingCompact) {
      rows.push([pendingCompact, field]);
      pendingCompact = null;
    } else {
      pendingCompact = field;
    }
  }

  flushPending();
  return rows;
};
